import { ConfigProvider, type GlobalToken } from 'antd';
import merge from 'lodash/merge';
import React, { useMemo } from 'react';
import {
  StyleSheetManager,
  ThemeProvider as StyledProvider,
} from 'styled-components';
import classic from './src/variants/classic';
import clean from './src/variants/clean';

interface ThemeProviderProps {
  children: React.ReactNode;
  theme: {
    color: string;
    fontFamily?: string;
    colorScheme?: string;
    antd?: GlobalToken;
  };
  configProvider?: {};
}

const ThemeProvider = function ({
  children,
  theme: { color, fontFamily, colorScheme = 'Clean', ...theme },
  configProvider = {},
}: ThemeProviderProps) {
  const colors = useMemo(() => {
    switch (colorScheme) {
      case 'Clean':
        return clean(color);
      default:
        return classic(color);
    }
  }, [color, colorScheme]);

  const font = useMemo(
    () => (fontFamily ? { token: { fontFamily } } : {}),
    [fontFamily],
  );

  const config = useMemo(
    () =>
      merge(
        {
          token: {
            borderRadius: parseInt(colors.smallRadius, 10),
            colorBorder: colors.borderColor,
            colorBorderSecondary: colors.borderColor,
            colorBorderDisabled: colors.disabledBorderColor,
            colorPrimary: colors.primaryColor,
            colorBgBase: colors.backgroundColor,
            colorError: colors.errorColor,
            colorSuccess: colors.successColor,
            colorTextBase: colors.onSurfaceColor,
            colorTextDescription: colors.neutralVariants[4],
            colorTextDisabled: colors.disabledTextColor,
          },
          components: {
            Layout: {
              headerBg: colors.secondarySurfaceColor,
            },
            Collapse: {
              colorFillAlter: colors.backgroundColor,
            },
            Avatar: {
              controlHeight: 40,
              controlHeightLG: 48,
            },
            Select: {
              controlItemBgActive: 'rgba(0 0 0 / 10%)',
            },
            Rate: {
              starColor: colors.starColor,
              starSize: 16,
            },
          },
        },
        font,
        configProvider,
      ),
    [font, colors, configProvider],
  );

  return (
    <StyleSheetManager enableVendorPrefixes shouldForwardProp={() => true}>
      <StyledProvider theme={{ ...theme, ...colors }}>
        <ConfigProvider
          theme={config}
          tag={{ styles: { root: { marginInlineEnd: 8 } } }}
        >
          {children}
        </ConfigProvider>
      </StyledProvider>
    </StyleSheetManager>
  );
};

export default ThemeProvider;
